import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { Puzzle, ArrowRight } from 'lucide-react';

import AuthContext from '../context/AuthContext';
import { usePluginContext } from '../context/PluginContext';
import API_BASE_URL from '../config/api';
import { devLog } from '../utils/devLog';
import { getPluginIcon } from '../utils/pluginIcons';
// HIGH-19: shared global axios that carries the request/response
// interceptors (token injection + global 401 session-expired handler).
import { authAxios as sharedAuthAxios } from '../utils/axiosConfig';

/**
 * PluginGuard
 *
 * Wraps plugin-gated routes. If the plugin key is enabled, renders children.
 * Otherwise renders an in-place "Install required" state so the designer can
 * install without leaving the page.
 *
 * Usage:
 *   <PluginGuard pluginKey="unified_orders">
 *     <UnifiedInbox />
 *   </PluginGuard>
 */
const PluginGuard = ({ pluginKey, children }) => {
  const { authToken } = useContext(AuthContext);
  const { isPluginEnabled, loading: pluginsLoading, refresh } = usePluginContext();

  const [pluginMeta, setPluginMeta] = useState(null);
  const [metaLoading, setMetaLoading] = useState(false);
  const [installing, setInstalling] = useState(false);

  const enabled = isPluginEnabled(pluginKey);

  // [HIGH-19] Build a per-token instance that still routes its 401s through
  // the global session-expired handler. `axios.create` alone would detach
  // the interceptors, so we re-attach the shared instance's handlers.
  const authAxios = useMemo(
    () => {
      const instance = axios.create({
        baseURL: API_BASE_URL,
        headers: { Authorization: `Bearer ${authToken?.access}` },
      });
      const reqHandlers = sharedAuthAxios.interceptors.request.handlers || [];
      reqHandlers.forEach((h) => {
        if (h) instance.interceptors.request.use(h.fulfilled, h.rejected);
      });
      const resHandlers = sharedAuthAxios.interceptors.response.handlers || [];
      resHandlers.forEach((h) => {
        if (h) instance.interceptors.response.use(h.fulfilled, h.rejected);
      });
      return instance;
    },
    [authToken]
  );

  // Load plugin metadata (name, icon, description) — only when gated.
  useEffect(() => {
    if (enabled || pluginsLoading || !authToken?.access) return;
    let cancelled = false;
    setMetaLoading(true);
    (async () => {
      try {
        const resp = await authAxios.get('/api/plugins/');
        const list = resp.data?.plugins || [];
        const match = list.find((p) => p.key === pluginKey);
        if (!cancelled) setPluginMeta(match || null);
      } catch (err) {
        devLog.error('PluginGuard: failed to load plugin metadata:', err);
      } finally {
        if (!cancelled) setMetaLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, pluginsLoading, authToken, authAxios, pluginKey]);

  const handleInstall = async () => {
    if (installing) return;
    setInstalling(true);
    try {
      await authAxios.post(`/api/plugins/${pluginKey}/install/`);
      await refresh();
      Swal.fire({
        title: 'Plugin installed',
        text: `${pluginMeta?.name || 'Plugin'} is now active.`,
        icon: 'success',
        confirmButtonColor: '#B40A0A',
        timer: 1600,
        showConfirmButton: false,
      });
    } catch (err) {
      devLog.error('Plugin install failed:', err);
      Swal.fire({
        title: 'Install failed',
        text:
          err.response?.data?.detail ||
          'Could not install the plugin. Please try again.',
        icon: 'error',
        confirmButtonColor: '#B40A0A',
      });
    } finally {
      setInstalling(false);
    }
  };

  // Still resolving whether plugin is enabled — small inline loader.
  if (pluginsLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
      </div>
    );
  }

  if (enabled) {
    return children;
  }

  // Gated: show install CTA.
  const Icon = getPluginIcon(pluginMeta?.icon) || Puzzle;
  const name = pluginMeta?.name || 'this feature';

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-4 md:p-8">
      <div className="max-w-md w-full bg-white border border-gray-200 rounded-2xl shadow-sm p-6 sm:p-8 text-center">
        <div
          className="mx-auto w-16 h-16 rounded-2xl flex items-center justify-center mb-5"
          style={{ background: '#B40A0A18', color: '#B40A0A' }}
        >
          <Icon size={30} />
        </div>
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
          {pluginMeta?.name || 'Plugin required'}
        </h2>
        <p className="text-gray-600 text-sm sm:text-base mb-4">
          {metaLoading
            ? 'Loading plugin details…'
            : `This feature requires the ${name} plugin.`}
        </p>
        {pluginMeta?.tagline && (
          <p className="text-gray-500 text-sm mb-6 italic">
            {pluginMeta.tagline}
          </p>
        )}

        <button
          onClick={handleInstall}
          disabled={installing || metaLoading}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-white font-semibold shadow-sm transition disabled:opacity-60 disabled:cursor-not-allowed"
          style={{ background: '#B40A0A' }}
        >
          {installing ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              Installing…
            </>
          ) : (
            <>
              Install {pluginMeta?.name || 'Plugin'}
              <ArrowRight size={16} />
            </>
          )}
        </button>

        <Link
          to="/tools"
          className="inline-block mt-4 text-sm text-gray-500 hover:text-gray-800 underline"
        >
          Browse all plugins
        </Link>

        <p className="mt-4 text-xs text-gray-400">
          Uninstall anytime — your data stays safe.
        </p>
      </div>
    </div>
  );
};

export default PluginGuard;
